import { useEffect, useState } from 'react'
import {
  CALIBRATION_GAMEWEEKS,
  type CalibrationState,
} from '../../calibration'
import {
  loadSkillMeterSeenState,
  saveSkillMeterSeenState,
} from '../../storage'
import { prefersReducedMotion } from '../../scoreCountUp'

// Calibration motion — must stay in step with the durations in
// Profile.css (.profile__skill-meter-progress-fill / --unlocking).
const CALIBRATION_FILL_MS = 480
/** Beat between the third segment settling and the card swapping to the tier
 * scale, so the fill reads as finished rather than cut off. */
const CALIBRATION_UNLOCK_HOLD_MS = 320
const CALIBRATION_UNLOCK_REVEAL_MS = 1600

export type UnlockPhase = 'none' | 'filling' | 'revealing'

/**
 * The Intuition Rating card's motion state machine: the bar's fill reveal, the
 * one-shot pulse when the rating has improved since this card was last seen, the
 * calibration segment ratchet, and the unlock choreography. Also
 * owns persisting the "last seen" baseline all three decisions read from.
 */
export function useSkillMeterMotion({
  pct,
  tierIndex,
  profileKey,
  calibration,
  isSelf,
}: {
  pct: number
  tierIndex: number
  profileKey: string
  calibration: CalibrationState
  /** The unlock reveal is a one-time personal moment — it never plays while
   * looking at someone else's profile. */
  isSelf: boolean
}) {
  // Both computed once per mount, mirroring the count-up's `playScoreCountUp`
  // pattern — plain reads via a useState initializer, safe under StrictMode's
  // double-render (and avoids setState-in-effect for isPulsing, which only ever
  // needs to be decided once, at mount).
  const [reducedMotion] = useState(prefersReducedMotion)
  const [seen] = useState(() => loadSkillMeterSeenState(profileKey))
  const [isFillRevealed, setIsFillRevealed] = useState(reducedMotion)
  const [isPulsing] = useState(() => {
    // No celebratory pulse while calibrating — the card has no tier or fill
    // to celebrate yet.
    if (reducedMotion || calibration.isCalibrating) return false
    return seen !== null && (pct > seen.pct || tierIndex > seen.tierIndex)
  })

  // How many calibration segments are currently drawn as complete. Starts at
  // whatever this profile's card last rendered so a gameweek scored since then
  // fills its segment instead of appearing already full.
  const [revealedProgress, setRevealedProgress] = useState(() => {
    if (reducedMotion || seen?.calibrationProgress === undefined) {
      return calibration.progress
    }
    return Math.min(seen.calibrationProgress, calibration.progress)
  })

  // Unlock reveal, fired by the third gameweek being scored —
  // in practice the tail of that gameweek's results reveal, since that's what
  // moves `calibration.progress` to 3. 'filling' still shows the calibrating
  // layout while the last segment fills; 'revealing' is the swap to the tier
  // scale, with the marker sliding out from the left edge.
  const [unlockPhase, setUnlockPhase] = useState<UnlockPhase>(() => {
    if (calibration.isCalibrating || reducedMotion || !isSelf) return 'none'
    // Never seen this card before, or it was already unlocked when we last
    // saw it — there is no transition to play, only a finished state to show.
    if (seen === null || seen.unlockPlayed) return 'none'
    const seenProgress = seen.calibrationProgress ?? CALIBRATION_GAMEWEEKS
    return seenProgress < CALIBRATION_GAMEWEEKS ? 'filling' : 'none'
  })

  const showCalibrating = calibration.isCalibrating || unlockPhase === 'filling'

  useEffect(() => {
    // Deliberately deferred until the tier bar is actually mounted: during the
    // unlock's 'filling' phase the bar isn't rendered yet, and flipping this
    // early would land it at its final width with nothing to transition.
    if (reducedMotion || showCalibrating) return
    const frame = requestAnimationFrame(() => setIsFillRevealed(true))
    return () => cancelAnimationFrame(frame)
  }, [reducedMotion, showCalibrating])

  useEffect(() => {
    if (revealedProgress >= calibration.progress) return
    const frame = requestAnimationFrame(() =>
      setRevealedProgress(calibration.progress)
    )
    return () => cancelAnimationFrame(frame)
  }, [revealedProgress, calibration.progress])

  useEffect(() => {
    if (unlockPhase === 'none') return
    const timer = setTimeout(
      () => setUnlockPhase(unlockPhase === 'filling' ? 'revealing' : 'none'),
      unlockPhase === 'filling'
        ? CALIBRATION_FILL_MS + CALIBRATION_UNLOCK_HOLD_MS
        : CALIBRATION_UNLOCK_REVEAL_MS
    )
    return () => clearTimeout(timer)
  }, [unlockPhase])

  useEffect(() => {
    // Persists what this card has actually shown as the new "last seen"
    // baseline: the pulse compares against it next mount, and
    // `calibrationProgress`/`unlockPlayed` keep the calibration fill and the
    // one-time unlock from replaying.
    saveSkillMeterSeenState(profileKey, {
      pct,
      tierIndex,
      calibrationProgress: calibration.progress,
      unlockPlayed: !calibration.isCalibrating && unlockPhase === 'none',
    })
  }, [
    profileKey,
    pct,
    tierIndex,
    calibration.progress,
    calibration.isCalibrating,
    unlockPhase,
  ])

  return {
    isFillRevealed,
    isPulsing,
    revealedProgress,
    unlockPhase,
    showCalibrating,
  }
}
